import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listCompanies, type Company } from "../../services/companies-api";
import { getAppCatalog, type AppCatalog } from "../../services/app-catalog-api";

/** FR-003: browse onboarded companies and jump into a new or existing one's setup. */
export function CompanyListPage() {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [catalog, setCatalog] = useState<AppCatalog | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    listCompanies().then(setCompanies);
    getAppCatalog()
      .then(setCatalog)
      .catch(() => setCatalog(null));
  }, []);

  const packageLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const pkg of catalog?.packages ?? []) map.set(pkg.code, pkg.label);
    return map;
  }, [catalog]);

  /**
   * What the company *bought*, packages first — "Digital Workspace - Advance, Service" — rather
   * than the derived list of every individual app that entitles them to. The derived list is
   * what eligibility matches on, but it runs to a dozen near-identical entries per row and buries
   * the one fact this table is for: which package each customer is on. The full list is on the
   * company's own page.
   */
  function purchaseSummary(company: Company): string {
    const parts = [
      ...company.purchasedPackages.map((code) => packageLabel.get(code) ?? code),
      ...company.purchasedSingularApps,
    ];
    // Before the catalogue loads there are no labels to show, so fall back to the derived apps
    // rather than flashing raw package codes like "dw-advance" at the CO Member.
    if (!catalog && company.purchasedPackages.length > 0) return company.apps.join(", ");
    return parts.join(", ");
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-sans text-xl text-ink">{t("companies.title", "Companies")}</h1>
        <button
          onClick={() => navigate("/co-member/companies/new")}
          className="rounded bg-brand px-4 py-2 font-sans text-surface"
        >
          {t("companies.newCompany", "+ New company")}
        </button>
      </div>

      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-surface-alt text-body">
            <th className="py-2">Name</th>
            <th>Sign-in slug</th>
            <th>{t("companies.packagesAndApps", "Packages & apps")}</th>
          </tr>
        </thead>
        <tbody>
          {companies.map((c) => (
            <tr
              key={c.id}
              className="cursor-pointer border-b border-surface-alt hover:bg-surface-alt"
              onClick={() => navigate(`/co-member/companies/${c.id}`)}
            >
              <td className="py-2 text-ink">{c.name}</td>
              <td>{c.slug}</td>
              <td>{purchaseSummary(c) || "—"}</td>
            </tr>
          ))}
          {companies.length === 0 && (
            <tr>
              <td colSpan={3} className="py-6 text-center text-body">
                No companies onboarded yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
